// Reader for the per-stock done-summary ZIP bundles
// (done_summary_bundle/{date}/{stock}.zip), produced by scheduler tasks 1b/1c.
//
// Downloads the whole archive (small per-stock zips, typically tens of KB) and
// extracts entries from the in-memory buffer — no Range/tail requests, which
// previously risked truncated reads on larger archives.
//
// The archive is re-fetched (and its central directory re-parsed) on every
// call that isn't already in flight, so switching filters and clicking "Show"
// always reflects the current contents of the blob — the browser's own HTTP
// cache makes repeat downloads of an unchanged blob cheap (served from disk
// cache / 304), so this still avoids re-transferring bytes in practice.

const EOCD_SIG = 0x06054b50;
const CENTRAL_DIR_SIG = 0x02014b50;
const LOCAL_HEADER_SIG = 0x04034b50;

interface CentralDirEntry {
  compressionMethod: number;
  compressedSize: number;
  localHeaderOffset: number;
}

interface ZipIndex {
  entries: Map<string, CentralDirEntry>;
  fullBuffer: Uint8Array;
}

function readU16(buf: Uint8Array, off: number): number {
  return (buf[off] ?? 0) | ((buf[off + 1] ?? 0) << 8);
}

function readU32(buf: Uint8Array, off: number): number {
  return ((buf[off] ?? 0) | ((buf[off + 1] ?? 0) << 8) | ((buf[off + 2] ?? 0) << 16) | ((buf[off + 3] ?? 0) << 24)) >>> 0;
}

/** Re-views a Uint8Array's bytes as a fresh ArrayBuffer, for Blob/Response constructors. */
function toArrayBuffer(data: Uint8Array): ArrayBuffer {
  return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
}

function findEOCD(buf: Uint8Array): { cdOffset: number; totalEntries: number } | null {
  for (let i = buf.length - 22; i >= 0; i--) {
    if (readU32(buf, i) === EOCD_SIG) {
      return {
        totalEntries: readU16(buf, i + 10),
        cdOffset: readU32(buf, i + 16),
      };
    }
  }
  return null;
}

function parseCentralDirectory(buf: Uint8Array, start: number, totalEntries: number): Map<string, CentralDirEntry> {
  const entries = new Map<string, CentralDirEntry>();
  let pos = start;
  for (let i = 0; i < totalEntries; i++) {
    if (pos + 46 > buf.length || readU32(buf, pos) !== CENTRAL_DIR_SIG) break;
    const compressionMethod = readU16(buf, pos + 10);
    const compressedSize = readU32(buf, pos + 20);
    const nameLength = readU16(buf, pos + 28);
    const extraLength = readU16(buf, pos + 30);
    const commentLength = readU16(buf, pos + 32);
    const localHeaderOffset = readU32(buf, pos + 42);
    const nameStart = pos + 46;
    const name = new TextDecoder().decode(buf.subarray(nameStart, nameStart + nameLength));
    entries.set(name, { compressionMethod, compressedSize, localHeaderOffset });
    pos = nameStart + nameLength + extraLength + commentLength;
  }
  return entries;
}

function extractFromLocalHeader(buf: Uint8Array, offset: number, entry: CentralDirEntry, entryName: string): Uint8Array {
  if (readU32(buf, offset) !== LOCAL_HEADER_SIG) {
    throw new Error(`Bad local file header for ${entryName}`);
  }
  const nameLength = readU16(buf, offset + 26);
  const extraLength = readU16(buf, offset + 28);
  const dataStart = offset + 30 + nameLength + extraLength;
  return buf.slice(dataStart, dataStart + entry.compressedSize);
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([toArrayBuffer(data)]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function fetchFullZip(zipUrl: string, signal?: AbortSignal): Promise<Uint8Array | null> {
  // Low priority so the (smaller, higher-value) OHLC parquet fetch — issued
  // around the same time — completes first even though both run concurrently.
  // cache: 'no-cache' memaksa revalidasi (If-Modified-Since/ETag) tiap fetch —
  // blob yang di-regen (mis. test_1e) tidak lagi disajikan basi dari disk cache,
  // tapi blob yang tidak berubah tetap murah via 304.
  const init: RequestInit = { priority: 'low', cache: 'no-cache' } as RequestInit;
  if (signal) init.signal = signal;
  const res = await fetch(zipUrl, init);
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} fetching ${zipUrl}`);
  }
  return new Uint8Array(await res.arrayBuffer());
}

async function loadZipIndex(zipUrl: string, signal?: AbortSignal): Promise<ZipIndex | null> {
  const buf = await fetchFullZip(zipUrl, signal);
  if (!buf) return null;

  const eocd = findEOCD(buf);
  if (!eocd) return null;
  const entries = parseCentralDirectory(buf, eocd.cdOffset, eocd.totalEntries);
  return { entries, fullBuffer: buf };
}

// Dedupes concurrent in-flight requests for the same archive (e.g. the rg/tn/ng
// combo fetches fired together by one "Show" click). Always removed once
// settled, so the next "Show" click re-fetches the archive — the browser's
// HTTP cache (Cache-Control/ETag) avoids re-transferring unchanged blobs.
const indexCache = new Map<string, Promise<ZipIndex | null>>();

function indexCacheKey(zipUrl: string): string {
  return zipUrl.split('?')[0] ?? zipUrl;
}

function getZipIndex(zipUrl: string, signal?: AbortSignal): Promise<ZipIndex | null> {
  const key = indexCacheKey(zipUrl);
  const cached = indexCache.get(key);
  if (cached) return cached;
  const p = loadZipIndex(zipUrl, signal);
  indexCache.set(key, p);
  p.finally(() => indexCache.delete(key));
  return p;
}

/**
 * Fetch and decompress a single entry from a ZIP archive at `zipUrl`. The whole
 * archive is downloaded and its central directory parsed, then the entry is
 * extracted from the in-memory buffer. Returns `null` if the archive or the
 * entry doesn't exist.
 */
export async function fetchZipEntry(zipUrl: string, entryName: string, signal?: AbortSignal): Promise<Uint8Array | null> {
  const index = await getZipIndex(zipUrl, signal);
  if (!index) return null;

  const entry = index.entries.get(entryName);
  if (!entry) return null;

  const raw = extractFromLocalHeader(index.fullBuffer, entry.localHeaderOffset, entry, entryName);
  return entry.compressionMethod === 8 ? await inflateRaw(raw) : raw;
}

/**
 * Fetch and decompress MULTIPLE entries from a ZIP archive at `zipUrl` with a
 * SINGLE download + central-directory parse (shared via getZipIndex). Returns
 * a Map of entry name to decompressed bytes (missing entries map to null), or
 * null if the archive itself doesn't exist.
 */
export async function fetchZipEntries(
  zipUrl: string,
  entryNames: string[],
  signal?: AbortSignal,
): Promise<Map<string, Uint8Array | null> | null> {
  const index = await getZipIndex(zipUrl, signal);
  if (!index) return null;

  const out = new Map<string, Uint8Array | null>();
  for (const name of new Set(entryNames)) {
    const entry = index.entries.get(name);
    if (!entry) {
      out.set(name, null);
      continue;
    }
    const raw = extractFromLocalHeader(index.fullBuffer, entry.localHeaderOffset, entry, name);
    out.set(name, entry.compressionMethod === 8 ? await inflateRaw(raw) : raw);
  }
  return out;
}
